import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useDebounce } from "hooks/useDebounce"
import { withRouter } from "react-router-dom"
import TableContainer from "components/Common/TableContainer"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as url from "../../helpers/url_helper"

import { Label, BrandName, UserCashback, State, MerchantName, MerchantLocation, Status } from "./offerCol"

import Breadcrumbs from "components/Common/Breadcrumb"

import {
    getPublishedOffers as onGetPublishedOffers,
    clearPublishedOffers,
    getOfferStateHistory as onGetOfferStateHistory,
    clearOfferStateHistory
} from "store/offers/actions"

import { useSelector, useDispatch } from "react-redux"
import AccessControlWrapper from "components/Common/PermissionButton"
import { PageTitle } from "constants/ui"
import CButton from "components/Common/CButton"
import OfferStateHistoryModal from "components/Offers/OfferStateHistoryModel"
import IconButton from "components/Common/IconButton"
import { getFirstTabPermission } from "helpers/tab_helper"

const PublishedOffersList = props => {
    document.title = PageTitle.PUBLISHED_OFFERS_LIST

    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [historyModal, setHistoryModal] = useState(false)
    const [selectedStatuses, setSelectedStatuses] = useState([])

    const dispatch = useDispatch()

    const { history } = props

    const { data, userMenu } = useSelector(state => ({
        data: state.offers,
        userMenu: state.userMenu?.userMenu || [],
    }))

    const firstTabPermission = getFirstTabPermission("PUBLISHED_OFFERS_DETAIL", userMenu)

    const columns = useMemo(
        () => [
            {
                Header: "Image",
                accessor: "image",
                disableFilters: true,
                filterable: true,

                accessor: cellProps => (
                    <>
                        {!cellProps.image ? (
                            <div className="avatar-xs">
                                <span className="avatar-title rounded-circle">
                                    {cellProps.label?.charAt(0) || "O"}
                                </span>
                            </div>
                        ) : (
                            <div>
                                <img
                                    className="rounded-circle avatar-xs"
                                    src={cellProps.image}
                                    alt=""
                                />
                            </div>
                        )}
                    </>
                ),
            },
            {
                Header: "Label",
                accessor: "label",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Label {...cellProps} />
                },
            },
            {
                Header: "Brand",
                accessor: "brand",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <BrandName {...cellProps} />
                },
            },
            {
                Header: "Merchant Name",
                accessor: "merchant.displayName",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <MerchantName {...cellProps} />
                },
            },
            {
                Header: "Merchant Location",
                accessor: "merchant.legalEntityAddress",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <MerchantLocation {...cellProps} />
                },
            },
            {
                Header: "Cashback %",
                accessor: "userCashbackPercentage",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <UserCashback {...cellProps} />
                },
            },
            {
                Header: "State",
                accessor: "state",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <State {...cellProps} />
                },
            },
            {
                Header: "Status",
                accessor: "offerStatus",
                filterable: true,
                disableFilters: true,
                Cell: cellProps => {
                    return <Status {...cellProps} />
                },
            },
            {
                Header: "Action",
                Cell: cellProps => {
                    return (
                        <div className="d-flex gap-2">
                            <AccessControlWrapper
                                actionType={firstTabPermission?.actionType || "get"}
                                contextUri={firstTabPermission?.contextUri || url.GET_PUBLISHED_OFFERS}
                                history={history}
                            >
                                <IconButton.View onClick={() => rowClick(null, cellProps.row)} />
                            </AccessControlWrapper>
                            <AccessControlWrapper
                                actionType="post"
                                contextUri={url.MANAGE_CURATOR_OFFER}
                                history={history}
                            >
                                <IconButton
                                    icon="bx bx-copy"
                                    color="secondary"
                                    title="Copy Offer"
                                    onClick={() => {
                                        const offerID = cellProps.row.original.offerID
                                        history.push(`/curator-offers/add?copyFrom=${offerID}&fromType=published`)
                                    }}
                                />
                            </AccessControlWrapper>
                            <AccessControlWrapper
                                actionType="get"
                                contextUri={url.GET_OFFER_STATE_HISTORY}
                                history={history}
                            >
                                <IconButton.History onClick={() => showHistoryModal(cellProps.row.original.offerID)} />
                            </AccessControlWrapper>
                        </div>
                    )
                },
            },
        ],
        []
    )

    useEffect(() => {
        dispatch(
            onGetPublishedOffers({
                headers: {
                    "input-parameters": {},
                },
                pagination: { startIndex: 0 },
            })
        )
    }, [dispatch])

    useEffect(() => {
        return () => {
            dispatch(clearPublishedOffers())
            dispatch(clearOfferStateHistory())
        }
    }, [dispatch])

    const rowClick = (e, row) => {
        const {
            original: { offerID },
        } = row
        history.push({
            pathname: `/published-offers/${offerID}`,
        })
    }

    const showHistoryModal = (offerID) => {
        setHistoryModal(true)
        dispatch(
            onGetOfferStateHistory({
                headers: {
                    "input-parameters": { offerID: offerID },
                },
            })
        )
    }

    const closeHistoryModal = () => {
        setHistoryModal(false)
        dispatch(clearOfferStateHistory())
    }

    const dispatchSearch = useCallback((value) => {
        setPage(1)
        dispatch(
            onGetPublishedOffers({
                headers: {
                    "input-parameters": {
                        keyword: value || "",
                        ...(selectedStatuses.length > 0 && { offerStatuses: selectedStatuses })
                    },
                },
                pagination: { startIndex: 0 },
            })
        )
    }, [dispatch, selectedStatuses])

    const [debouncedSearch] = useDebounce(dispatchSearch)

    const handleSearchChange = e => {
        const { value } = e.target
        setSearch(value)
        debouncedSearch(value)
    }

    const handlePageChange = page => {
        const startIndex = (page - 1) * 10
        setPage(page)
        dispatch(
            onGetPublishedOffers({
                headers: {
                    "input-parameters": {
                        keyword: search || "",
                        ...(selectedStatuses.length > 0 && { offerStatuses: selectedStatuses })
                    },
                },
                pagination: { startIndex },
            })
        )
    }

    const handleRefresh = () => {
        dispatch(
            onGetPublishedOffers({
                headers: {
                    "input-parameters": {
                        keyword: search || "",
                        ...(selectedStatuses.length > 0 && { offerStatuses: selectedStatuses })
                    },
                },
                pagination: { startIndex: (page - 1) * 10 },
            })
        )
    }

    const handleStatusChange = (status) => {
        const newStatuses = selectedStatuses.includes(status)
            ? selectedStatuses.filter(s => s !== status)
            : [...selectedStatuses, status]

        setSelectedStatuses(newStatuses)

        dispatch(
            onGetPublishedOffers({
                headers: {
                    "input-parameters": {
                        keyword: search || "",
                        ...(newStatuses.length > 0 && { offerStatuses: newStatuses })
                    },
                },
                pagination: { startIndex: 0 },
            })
        )
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title={PageTitle.PUBLISHED_OFFERS_LIST}
                        titleLink="/published-offers"
                        breadcrumbItem={PageTitle.PUBLISHED_OFFERS_LIST}
                    />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md="6">
                                            <div className="d-flex gap-3 align-items-center">
                                                <input
                                                    type="text"
                                                    placeholder="search published offers"
                                                    onChange={handleSearchChange}
                                                    className="form-control w-50 rounded-pill"
                                                />
                                                <div className="d-flex gap-2 align-items-center">
                                                    <div className="form-check form-check-primary">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id="live"
                                                            //  live,coming soon,expired
                                                            checked={selectedStatuses.includes('live')}
                                                            onClick={() => handleStatusChange('live')}
                                                        />
                                                        <label className="form-check-label" htmlFor="live">
                                                            Live
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-primary">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id="coming_soon"
                                                            checked={selectedStatuses.includes('coming soon')}
                                                            onClick={() => handleStatusChange('coming soon')}
                                                        />
                                                        <label className="form-check-label" htmlFor="coming_soon">
                                                            Coming Soon
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-primary">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id="expired"
                                                            checked={selectedStatuses.includes('expired')}
                                                            onClick={() => handleStatusChange('expired')}
                                                        />
                                                        <label className="form-check-label" htmlFor="expired">
                                                            Expired
                                                        </label>
                                                    </div>

                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="6">
                                            <div className="text-sm-end">
                                                {/* Future: Add Offer button can be added here */}
                                            </div>
                                        </Col>
                                    </Row>

                                    <TableContainer
                                        columns={columns}
                                        data={data?.publishedError?.message ? [] : data.publishedOffers}
                                        isGlobalFilter={false}
                                        isAddUserList={true}
                                        rowClick={rowClick}
                                        protectedRowClick={true}
                                        contextUri={url.GET_PUBLISHED_OFFERS}
                                        actionType="get"
                                        customPageSize={10}
                                        className="custom-header-css"
                                        columnFilter={false}
                                        changePageSize={false}
                                        onPageChange={handlePageChange}
                                        currentPage={page}
                                        hasNextPage={!data?.publishedError?.message && data.publishedOffers?.length >= 10}
                                        onRefresh={handleRefresh}
                                        refreshContextUri={url.GET_PUBLISHED_OFFERS}
                                        refreshActionType="get"
                                        history={history}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* History Modal */}
            <OfferStateHistoryModal
                isOpen={historyModal}
                toggle={closeHistoryModal}
                historyData={data.offerStateHistory}
                isLoading={data.isLoadingOfferStateHistory}
            />
        </React.Fragment>
    )
}

export default withRouter(PublishedOffersList)